import { useExamCenter } from '@exam-center/exam-center-details';
import {
  ExamLocationRequestWorkflowStepCode,
  FilterOperator,
  OrderType,
  useSearchExamRoomRequestInfosQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  type DataGridProps,
  DescriptionOutlinedIcon,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
} from '@shared/components';

import {
  BaseExamLocationRequestList,
  inReviewColumns,
  type InReviewExamLocationRequest,
} from '../../components';
import { useExamLocationRequestListSearchParams } from '../../hooks';

export default function InReviewExamLocationRequestList() {
  const navigate = useNavigate();

  const { examCenter } = useExamCenter();

  const [
    { province, locationName, page, pageSize, sortBy, sortOrder },
    { changePageNumber, changePageSize, changeSorting },
  ] = useExamLocationRequestListSearchParams();

  const handleSortModelChange: DataGridProps['onSortModelChange'] = (sortModel) => {
    const sort = sortModel[0]?.sort ?? 'desc';
    changeSorting({ sortOrder: sort, sortBy: 'requestedAt' });
  };

  const { data, loading } = useSearchExamRoomRequestInfosQuery({
    variables: {
      searchExamRoomRequestInfosInput: {
        provinceId: province,
        searchLocationName: locationName,
        paging: {
          currentPage: page,
          pageSize,
        },
        order: {
          field: sortBy ?? 'requestedAt',
          type: sortOrder === 'asc' ? OrderType.Asc : OrderType.Desc,
        },
        filter: {
          conditions: [
            {
              field: 'examCenterCode',
              value: examCenter.code,
              operator: FilterOperator.Equal,
            },
            {
              field: 'step',
              value: [
                ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_1,
                ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_2,
              ],
              operator: FilterOperator.In,
            },
          ],
        },
      },
    },
  });

  const examRoomRequests = data?.ecSearchExamRoomRequestInfos.examRoomRequestInfos ?? [];

  const totalCount = data?.ecSearchExamRoomRequestInfos.total ?? 0;
  const examLocationRequestItems: InReviewExamLocationRequest[] = examRoomRequests.map((item) => ({
    id: item.id,
    refCode: item.taskCode ?? '-',
    buildingName:
      item.examLocationRequestInfo.buildingName ??
      item.examLocationRequestInfo.building?.nameTh ??
      '-',
    locationName: item.examLocationRequestInfo.nameTh ?? '-',
    province: item.examLocationRequestInfo.province.nameTh,
    floor: item.floor,
    roomName: item.name,
    roomNumber: item.roomNumber ?? '-',
    requestedAt: item.requestedAt,
    status: 'in-review',
  }));

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<InReviewExamLocationRequest>,
  ) => {
    navigate(`/:examCenterCode/exam-location/requests/:roomRequestId`, {
      params: {
        roomRequestId: params.row.id,
        examCenterCode: examCenter.code,
      },
    });
  };

  const isSearchOrFiltering = (locationName?.length ?? 0) > 0 || Boolean(province);

  return (
    <BaseExamLocationRequestList
      columns={inReviewColumns}
      loading={loading}
      rows={examLocationRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchOrFiltering) {
            return <NoRowsOverlay />;
          }
          return (
            <NoRowsOverlay
              title='ยังไม่มีคำขออนุมัติสถานที่สอบที่อยู่ระหว่างอนุมัติ'
              icon={DescriptionOutlinedIcon}
            />
          );
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={handleSortModelChange}
    />
  );
}
